import { ControlledTooltip, MenuButton } from '@shared/components';
import {
  ESubmissionItemStatus,
  ESubmissionStatus,
  TResolvedSubmissionItem,
  TSubmission,
} from '../../../types/submissions.types';
import { formatSubmissionStatus } from '../../SubmissionStatusBadge';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, tokens, Tooltip } from '@fluentui/react-components';
import { SquareFilled, SquareRegular } from '@fluentui/react-icons';
import { SUBMISSION_ITEM_STATUS_COLORS } from '../constants';
import { SignInContext, TUser, USER_GROUPS } from '../../../../users';
import { useContext } from 'react';
import { TUpdateSubmissionItemDialogState } from '../../../types/dialogs.types';
import { ALL_SUBMISSION_ITEM_STATES } from '../../../constants';
import { useHasAssigneePermissions, useHasFullPermissions } from '../../../helpers/getPermissions.helper';

// --- ICON:

type TIconProps = {
  status: ESubmissionItemStatus;
  size?: 'default' | 'small' | 'medium';
};

export const SubmissionItemStatusIcon = ({ status, size = 'default' }: TIconProps) => {
  const fontSize = size === 'small' ? 16 : size === 'medium' ? 20 : undefined;

  return status === ESubmissionItemStatus.BACKLOG ? (
    <SquareRegular fontSize={fontSize} />
  ) : (
    <SquareFilled color={SUBMISSION_ITEM_STATUS_COLORS[status]} fontSize={fontSize} />
  );
};

// --- STATUS BUTTON:

type TProps = {
  submission: TSubmission;
  resolvedSubmissionItem: TResolvedSubmissionItem;
  setUpdateSubmissionItemDialogState: (state?: TUpdateSubmissionItemDialogState) => void;
};

const SubmissionItemStatus = ({ submission, resolvedSubmissionItem, setUpdateSubmissionItemDialogState }: TProps) => {
  const { status } = resolvedSubmissionItem;

  const appearance = status === ESubmissionItemStatus.BACKLOG ? 'secondary' : 'primary';
  const variant = [ESubmissionItemStatus.IN_PROGRESS, ESubmissionItemStatus.IN_REVIEW].includes(status)
    ? 'blue'
    : 'default';

  // --- PERMISSIONS:
  const isSubmissionInPreparation = submission.status === ESubmissionStatus.IN_PREPARATION;
  const hasFullPermissions = useHasFullPermissions(submission);
  const hasAssigneePermissions = useHasAssigneePermissions(hasFullPermissions, resolvedSubmissionItem);

  const button = (
    <MenuButton size="small" appearance={appearance} variant={variant} onClick={(e) => e.stopPropagation()}>
      {formatSubmissionStatus(status)}
    </MenuButton>
  );

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        {isSubmissionInPreparation && hasAssigneePermissions ? (
          button // we cannot use ControlledTooltip here, because the menu needs a trigger element
        ) : (
          <Tooltip
            content={
              !isSubmissionInPreparation
                ? 'Submission is not in status "In preparation".'
                : !hasAssigneePermissions
                  ? 'You do not have the required permissions.'
                  : ''
            }
            relationship="description"
          >
            {button}
          </Tooltip>
        )}
      </MenuTrigger>
      {isSubmissionInPreparation && hasAssigneePermissions ? (
        <MenuPopover>
          <MenuList>
            {ALL_SUBMISSION_ITEM_STATES.filter((newStatus) => newStatus !== status).map((newStatus) => {
              const hasTransitionPermissions = hasFullPermissions || ESubmissionItemStatus.ACCEPTED !== newStatus;
              return (
                <ControlledTooltip
                  enable={!hasTransitionPermissions}
                  content="Only submission leads and admins can perform this status transition."
                  relationship="inaccessible"
                  key={newStatus}
                >
                  <MenuItem
                    icon={<SubmissionItemStatusIcon status={newStatus} />}
                    key={newStatus}
                    disabled={!hasTransitionPermissions}
                    onClick={(e) => {
                      setUpdateSubmissionItemDialogState({
                        isOpen: true,
                        submissionInfo: submission,
                        submissionItem: resolvedSubmissionItem,
                        item: resolvedSubmissionItem.resolvedItem,
                        newStatus,
                      });
                      e.stopPropagation();
                    }}
                  >
                    {formatSubmissionStatus(newStatus)}
                  </MenuItem>
                </ControlledTooltip>
              );
            })}
          </MenuList>
        </MenuPopover>
      ) : (
        <></>
      )}
    </Menu>
  );
};

export default SubmissionItemStatus;
